import QueryClient from "@viz-ui/services/query/queryClient/queryClient";
import QueryGenerator from "@viz-ui/services/query/queryGenerator/queryGenerator";
import QueryErrorInterpreter from "@viz-ui/services/query/queryErrorInterpreter/queryErrorInterpreter.service";

// Import viz-ui dependencies directly - all already available in viz-ui
import ChartDataAdapter from "@viz-ui/models/chart/chartDataAdapter";
import FieldFormatAdapter from "@viz-ui/models/field/fieldFormatAdapter";
import DataFilter from "@viz-ui/services/charts/dataFilter/dataFilterService";
import MultiSelectFilterStore from "@viz-ui/services/quickMenu/MultiSelectFilterStoreService";
import DataModel from "@viz-ui/services/charts/DataModel/dataModelService";
import Sorter from "@viz-ui/services/sorters/sorter";
import SummaryTableAdapter from "@viz-ui/models/summaryTable/summaryTableAdapter";

/**
 * Chart Data Query Backend Service (Migrated to viz-ui)
 * 
 * This service handles chart data requests using the new query service architecture.
 * It provides a framework-independent implementation that can be used across different
 * frontend frameworks while maintaining compatibility with the existing Angular codebase.
 * 
 * Key improvements over the original Angular implementation:
 * - Framework independence (no Angular DI dependency)
 * - Better error handling with detailed error messages
 * - Improved logging and debugging capabilities
 * - Direct access to viz-ui services without injection complexity
 * - Enhanced maintainability and testability
 */
export class ChartDataQueryBackendService {
  
  constructor(appConfig = null) {
    // Initialize DataFilter with required dependencies
    // Use provided appConfig (from Angular) or fallback to window.AppConfig
    const AppConfig = appConfig || (typeof window !== 'undefined' && window.AppConfig) || { features: {} };
    this.dataFilter = new DataFilter(AppConfig, MultiSelectFilterStore, DataModel);
  }
  
  /**
   * Main method for requesting chart data
   * 
   * @param {Object} table - Table object with id() method
   * @param {string} chartType - Type of chart (e.g., "BarChart", "SummaryTable")
   * @param {Object} config - Configuration object with filterConfig and formatting
   * @param {Object} dataConfig - Chart data configuration
   * @param {Object} globalFilters - Global filter settings
   * @param {Object} scope - Query scope information
   * @param {Object} displayConfig - Display configuration options
   * @returns {Promise} Promise resolving to processed chart data
   */
  chartDataRequest(table, chartType, { filterConfig, formatting }, dataConfig, globalFilters, scope, displayConfig) {
    return new Promise((resolve, reject) => {
      try {
        // Process filter configuration using viz-ui DataFilter instance
        let { filterList } = this.dataFilter.getFilterRequestBody(filterConfig);

        // Generate query using viz-ui QueryGenerator
        let aggColumnQuery = QueryGenerator.chartData({
          tableId: table.id(),
          chartType,
          filterList,
          dataConfig,
          globalFilters,
          scope,
        });

        QueryClient.query(aggColumnQuery)
          .then(response => {
            try {
              const chartData = this.deserializeChartDataModel(
                chartType,
                dataConfig,
                table,
                formatting,
                response,
                displayConfig
              );
              resolve(chartData);
            } catch (deserializationError) {
              reject(deserializationError);
            }
          })
          .catch(response => {
            const errorCode = QueryErrorInterpreter.errorCode(response);
            reject(errorCode);
          });
      } catch (setupError) {
        reject(setupError);
      }
    });
  }

  /**
   * Deserializes chart data based on chart type
   * 
   * @param {string} chartType - Type of chart
   * @param {Object} dataConfig - Chart data configuration
   * @param {Object} table - Table object
   * @param {Object} formatting - Field formatting information
   * @param {Object} response - Raw response from query service
   * @param {Object} displayConfig - Display configuration
   * @returns {Object} Processed chart data ready for visualization
   */
  deserializeChartDataModel(chartType, dataConfig, table, formatting, response, displayConfig) {
    if (chartType === "SummaryTable") {
      // Use viz-ui FieldFormatAdapter for field formatting
      const fieldFormatMap = FieldFormatAdapter.deserializeFieldFormatMap(formatting);
      
      // Sort summary table data using viz-ui Sorter
      const json = this.sortSummaryTableData(dataConfig, response.data.table);
      
      // Determine if totals should be shown
      const showTotals = displayConfig.showTotals === false ? false : true;

      // Use viz-ui SummaryTableAdapter for final processing
      return SummaryTableAdapter.deserializeSummaryTable(dataConfig, fieldFormatMap, json, showTotals);
    }
    
    // For all other chart types, use viz-ui ChartDataAdapter
    const aggregationData = response.data.table;
    return ChartDataAdapter.deserializeChartData(chartType, table, dataConfig, aggregationData);
  }

  /**
   * Sorts summary table data using viz-ui Sorter
   * 
   * @param {Object} dataConfig - Chart data configuration
   * @param {Object} json - Raw JSON data to sort
   * @returns {Object} Sorted data with subtotalData and summarizedData
   */
  sortSummaryTableData(dataConfig, json) {
    // Sort subtotal data if chart columns are defined
    let chartColumn = dataConfig.chartColumns.length ? dataConfig.chartColumns[0] : undefined;
    let subtotalData = chartColumn
      ? Sorter.sort(json.subtotalData, {
          dataType: chartColumn.type,
          valueParser: item => item[chartColumn.fieldName],
        })
      : json.subtotalData;

    // Sort summarized data by chart rows
    let { chartRows } = dataConfig;
    let rowTypes = chartRows.map(row => row.type);
    
    let summarizedData = json.summarizedData.sort((a, b) => {
      let i;
      let result;
      for (i = 0; i < rowTypes.length; i++) {
        let comparator = Sorter.getTypedComparator({ dataType: rowTypes[i] });
        result = comparator(a[chartRows[i].fieldName], b[chartRows[i].fieldName]);
        if (result !== 0) {
          return result;
        }
      }
      return 0;
    });

    return {
      subtotalData: subtotalData,
      summarizedData: summarizedData,
    };
  }
}

/**
 * Export a singleton instance for backward compatibility with existing code
 * This allows the service to be used both as a class (new pattern) and as a singleton (old pattern)
 */
export const chartDataQueryBackendService = new ChartDataQueryBackendService();

/**
 * Default export for easy importing
 */
export default ChartDataQueryBackendService;
